// src/hooks/mui/useConfirmDialog.js
import { useState, useCallback } from 'react';
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export function useConfirmDialog() {
  const { t } = useTranslation(['common']);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    severity: 'warning',
    onConfirm: () => {},
    onCancel: () => {},
  });

  // Defaults are resolved here rather than as parameter defaults so callers that
  // omit them still get translated buttons instead of hardcoded English.
  const showConfirmDialog = useCallback((options) => {
    const {
      title = t('common:confirm_dialog.title'),
      message = t('common:confirm_dialog.message'),
      confirmText = t('common:buttons.confirm'),
      cancelText = t('common:buttons.cancel'),
      severity = 'warning',
      onConfirm = () => {},
      onCancel = () => {},
    } = options;

    setConfirmDialog({
      open: true,
      title,
      message,
      confirmText,
      cancelText,
      severity,
      onConfirm,
      onCancel,
    });
  }, [t]);

  const hideConfirmDialog = useCallback(() => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    confirmDialog.onConfirm();
    hideConfirmDialog();
  }, [confirmDialog.onConfirm, hideConfirmDialog]);

  const handleCancel = useCallback(() => {
    confirmDialog.onCancel();
    hideConfirmDialog();
  }, [confirmDialog.onCancel, hideConfirmDialog]);

  const getButtonColor = useCallback(() => {
    switch (confirmDialog.severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      case 'success':
        return 'success';
      default:
        return 'primary';
    }
  }, [confirmDialog.severity]);

  // Return the dialog component along with the control functions
  const ConfirmDialog = useCallback(() => {
    return (
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit">
            {confirmDialog.cancelText}
          </Button>
          <Button 
            onClick={handleConfirm} 
            color={getButtonColor()}
            variant="contained"
            autoFocus
          >
            {confirmDialog.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [confirmDialog, handleConfirm, handleCancel, getButtonColor]);

  return {
    showConfirmDialog,
    hideConfirmDialog,
    ConfirmDialog,
    // Legacy API for backward compatibility
    confirmDialog,
    handleConfirm,
    handleCancel,
  };
}